/**
 * Event handlers for ConfirmModal
 * Extracted to reduce component size
 */

export interface ConfirmModalHandlers {
	handleConfirm: () => Promise<void>;
	handleCancel: () => void;
	handleClose: () => void;
}

export interface BuildHandlersParams {
	requireConfirmation?: {
		enabled: boolean;
		expectedValue: string;
	};
	confirmationValue: string;
	setConfirmationValue: (value: string) => void;
	confirmButton?: {
		onClick: () => void | Promise<void>;
	};
	onConfirm?: () => void | Promise<void>;
	cancelButton?: {
		onClick?: () => void;
	};
	onCancel?: () => void;
	onClose: () => void;
	/** When true, closes the modal before awaiting confirm (avoids stuck backdrop on hung PUT/POST/DELETE). */
	closeOnConfirmStart: boolean;
}

function isRequireConfirmationSatisfied(
	requireConfirmation: BuildHandlersParams["requireConfirmation"],
	confirmationValue: string,
): boolean {
	const ev = requireConfirmation?.enabled ? requireConfirmation.expectedValue : null;
	if (ev == null) {
		return true;
	}
	return typeof ev === "string" && ev.trim().length > 0 && confirmationValue === ev;
}

async function runConfirmClick(params: {
	confirmButton?: BuildHandlersParams["confirmButton"];
	onConfirm?: BuildHandlersParams["onConfirm"];
	onClose: () => void;
	closeOnConfirmStart: boolean;
}): Promise<void> {
	const clickHandler = params.confirmButton?.onClick ?? params.onConfirm;
	if (!clickHandler) {
		return;
	}
	if (params.closeOnConfirmStart) {
		params.onClose();
	}
	await clickHandler();
}

/**
 * Builds event handlers for ConfirmModal
 */
export function buildConfirmModalHandlers(params: BuildHandlersParams): ConfirmModalHandlers {
	const {
		requireConfirmation,
		confirmationValue,
		setConfirmationValue,
		confirmButton,
		onConfirm,
		cancelButton,
		onCancel,
		onClose,
		closeOnConfirmStart,
	} = params;

	const handleConfirm = async (): Promise<void> => {
		if (!isRequireConfirmationSatisfied(requireConfirmation, confirmationValue)) {
			return;
		}
		await runConfirmClick({ confirmButton, onConfirm, onClose, closeOnConfirmStart });
	};

	const handleCancel = (): void => {
		if (cancelButton?.onClick) {
			cancelButton.onClick();
		} else if (onCancel) {
			onCancel();
		} else {
			onClose();
		}
	};

	const handleClose = (): void => {
		setConfirmationValue("");
		onClose();
	};

	return { handleConfirm, handleCancel, handleClose };
}
